(() => {
  if (customElements.get('norifox-product')) return;
  class NorifoxProduct extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.config = JSON.parse(this.querySelector('[data-product-config]').textContent);
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.quantity = this.querySelector('[data-quantity]');
      this.options = [...this.querySelectorAll('[data-option-position]')];
      this.media = [...this.querySelectorAll('[data-media]')];
      this.busy = false;
      this.on('change', event => {
        if (event.target.matches('[data-option-input]')) this.updateVariant(true);
        if (event.target.matches('[data-quantity]')) this.updateQuantity();
      });
      this.on('input', event => {
        if (event.target.matches('[data-quantity]')) this.updateQuantity();
      });
      this.on('click', event => this.handleClick(event));
      this.form.addEventListener('submit', event => this.submit(event), { signal: this.signal });
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('click', event => {
          if (event.target === dialog) {
            const rect = dialog.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
          }
        }, { signal: this.signal });
      });
      this.on('cart:update', () => this.setBusy(false));
      this.on('cart:error', event => {
        this.setBusy(false);
        if (event.detail?.description || event.detail?.message) this.showError(event.detail.description || event.detail.message);
      });
      this.updateVariant(false);
      this.showMedia(this.querySelector('[data-media]:not([hidden])')?.dataset.media);
      this.classList.add('nfp-ready');
      this.observePurchase();
      this.enableSwipe();
      if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      clearTimeout(this.busyTimer);
      this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
    }
    on(name, handler) { this.addEventListener(name, handler, { signal: this.signal }); }
    updateVariant(updateUrl) {
      const values = this.options.map(option => option.querySelector(':checked')?.value);
      this.variant = this.options.length
        ? this.config.variants.find(variant => variant.options.every((value, index) => value === values[index]))
        : this.config.variants.find(variant => variant.id === this.config.defaultVariant);
      this.options.forEach((option, index) => {
        const checked = option.querySelector(':checked');
        option.querySelector('[data-option-value]').textContent = checked?.dataset.label || '';
        option.querySelectorAll('[data-option-input]').forEach(input => {
          const candidates = this.config.variants.filter(variant => variant.options[index] === input.value && values.slice(0, index).every((value, previous) => variant.options[previous] === value));
          input.closest('label').dataset.unavailable = String(!candidates.some(variant => variant.available));
        });
      });
      this.variantInput.value = this.variant ? this.variant.id : '';
      this.variantInput.disabled = !this.variant;
      const label = this.variant ? (this.variant.available ? this.config.labels.add : this.config.labels.soldOut) : this.config.labels.unavailable;
      this.querySelectorAll('[data-add], [data-sticky-add]').forEach(button => { button.textContent = label; button.disabled = !this.variant?.available || this.busy; });
      this.querySelector('[data-status]').textContent = !this.variant ? this.config.labels.unavailable : !this.variant.available ? this.config.labels.soldOut : this.dataset.preorder === 'true' ? this.config.labels.preorder : '';
      if (this.variant) {
        this.querySelectorAll('[data-price],[data-sticky-price]').forEach(node => node.textContent = this.variant.price);
        const compare = this.querySelector('[data-compare]');
        if (compare) { compare.textContent = this.variant.compare; compare.hidden = !this.variant.onSale; }
        this.quantity.min = this.variant.minimum;
        this.quantity.step = this.variant.step;
        if (this.variant.maximum != null) this.quantity.max = this.variant.maximum; else this.quantity.removeAttribute('max');
        this.normalizeQuantity();
        if (this.variant.media) this.showMedia(String(this.variant.media));
      } else {
        this.querySelector('[data-compare]')?.setAttribute('hidden', '');
      }
      const express = this.querySelector('[data-express]');
      if (express) express.hidden = !this.variant?.available;
      this.updateQuantity();
      this.clearError();
      this.dispatchEvent(new CustomEvent('norifox:variant-change', { bubbles: true, detail: { variant: this.variant } }));
      if (updateUrl) {
        const url = new URL(window.location.href);
        if (this.variant) url.searchParams.set('variant', this.variant.id); else url.searchParams.delete('variant');
        window.history.replaceState({}, '', url);
        this.querySelector('[data-announcement]').textContent = [...values, label].join(', ');
      }
    }
    normalizeQuantity() {
      const min = Number(this.quantity.min) || 1;
      const step = Number(this.quantity.step) || 1;
      const max = Number(this.quantity.max) || Infinity;
      const number = Number(this.quantity.value);
      let next = Number.isFinite(number) && number > 0 ? number : min;
      next = Math.max(min, min + Math.floor((next - min) / step) * step);
      if (next > max) next = min + Math.floor((max - min) / step) * step;
      this.quantity.value = String(Math.max(min, next));
    }
    updateQuantity() {
      const labels = this.options.map(option => option.querySelector(':checked')?.dataset.label).filter(Boolean);
      if (Number(this.quantity.value) > 1) labels.push(`${this.config.labels.quantity} ${this.quantity.value}`);
      const summary = this.querySelector('[data-sticky-options]');
      if (summary) summary.textContent = labels.join(' · ');
      const change = this.querySelector('[data-change-options]');
      if (change) change.hidden = !this.options.length && this.quantity.type === 'hidden';
    }
    showMedia(id) {
      if (!id || !this.media.some(media => media.dataset.media === id)) return;
      this.media.forEach(media => {
        media.hidden = media.dataset.media !== id;
        if (media.hidden) media.querySelectorAll('video').forEach(video => video.pause());
      });
      this.querySelectorAll('[data-show-media]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.showMedia === id)));
      const index = this.media.findIndex(media => media.dataset.media === id);
      const counter = this.querySelector('[data-counter]');
      if (counter) counter.textContent = `${index + 1} / ${this.media.length}`;
      const zoom = this.querySelector('[data-open-zoom]');
      if (zoom) zoom.hidden = this.media[index].dataset.type !== 'image';
    }
    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-show-media')) this.showMedia(button.dataset.showMedia);
      if (button.hasAttribute('data-open-guide')) this.openDialog('[data-guide-dialog]');
      if (button.hasAttribute('data-open-contents')) this.openDialog('[data-contents-dialog]');
      if (button.hasAttribute('data-open-zoom')) {
        const media = this.media.find(node => !node.hidden);
        if (media?.dataset.zoom) {
          const image = this.querySelector('[data-zoom-image]');
          image.width = Number(media.dataset.zoomWidth) || 1600;
          image.height = Number(media.dataset.zoomHeight) || 1600;
          image.src = media.dataset.zoom;
          image.alt = media.querySelector('img')?.alt || this.dataset.productTitle;
          this.openDialog('[data-zoom-dialog]');
        }
      }
      if (button.hasAttribute('data-close-dialog')) button.closest('dialog').close();
      if (button.hasAttribute('data-change-options')) {
        const target = this.options[0] || this.quantity;
        target.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        (this.options[0]?.querySelector(':checked') || this.quantity).focus({ preventScroll: true });
      }
    }
    openDialog(selector) {
      const dialog = this.querySelector(selector);
      if (dialog && !dialog.open) dialog.showModal();
    }
    observePurchase() {
      this.observer?.disconnect();
      const controls = this.querySelector('[data-buying-controls]');
      this.observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        this.classList.toggle('nfp-purchase-scrolled', !entry.isIntersecting && entry.boundingClientRect.bottom < 0);
      });
      this.observer.observe(controls);
    }
    enableSwipe() {
      if (this.dataset.swipe !== 'true' || this.media.length < 2) return;
      const gallery = this.querySelector('[data-gallery]');
      gallery.addEventListener('touchstart', event => { this.touch = [event.changedTouches[0].clientX, event.changedTouches[0].clientY]; }, { passive: true, signal: this.signal });
      gallery.addEventListener('touchend', event => {
        if (!this.touch || event.target.closest('video,iframe,model-viewer,button')) return;
        const dx = event.changedTouches[0].clientX - this.touch[0];
        const dy = event.changedTouches[0].clientY - this.touch[1];
        if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return;
        const index = this.media.findIndex(media => !media.hidden);
        const next = (index + (dx < 0 ? 1 : -1) + this.media.length) % this.media.length;
        this.showMedia(this.media[next].dataset.media);
      }, { passive: true, signal: this.signal });
    }
    setBusy(busy) {
      this.busy = busy;
      clearTimeout(this.busyTimer);
      this.form.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-add],[data-sticky-add]').forEach(button => {
        button.disabled = busy || !this.variant?.available;
        button.textContent = busy ? this.config.labels.adding : !this.variant ? this.config.labels.unavailable : this.variant.available ? this.config.labels.add : this.config.labels.soldOut;
      });
    }
    showError(message) {
      const error = this.querySelector('[data-error]');
      error.textContent = message;
      error.hidden = false;
      error.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
    clearError() { this.querySelector('[data-error]').hidden = true; }
    async submit(event) {
      if (event.submitter?.closest('[data-express]')) return;
      event.preventDefault();
      if (this.busy) return;
      if (!this.variant?.available || !this.variantInput.value) { this.showError(this.config.labels.unavailable); return; }
      if (!this.form.reportValidity()) return;
      this.clearError();
      this.normalizeQuantity();
      this.updateQuantity();
      if (this.dataset.cartType === 'drawer') {
        const native = this.querySelector('product-form-component');
        if (typeof native?.handleSubmit === 'function') {
          native.handleSubmit(event);
          this.setBusy(true);
          this.busyTimer = setTimeout(() => this.setBusy(false), 12000);
          return;
        }
      }
      this.setBusy(true);
      try {
        const body = new FormData(this.form);
        const response = await fetch(this.dataset.addUrl, { method: 'POST', body, headers: { Accept: 'application/json' } });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || this.config.labels.error);
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        this.setBusy(false);
        this.showError(error.message || this.config.labels.error);
      }
    }
  }
  customElements.define('norifox-product', NorifoxProduct);
})();


